import type { OptionsMap } from '@openx/types/options.ts';
import merge from 'lodash/merge';
import { useEffect } from 'react';
import { useRequiredParamsSetter } from '../context/TargetingSettingsContext.tsx';
import type { RequiredParamsType, Section } from '../context/types.ts';
import { filterOptions } from '../utils/filterOptions.ts';

type UseSyncInitialTargetingProps = {
  isLoading: boolean;
  sectionKey: string;
  initialState: Section;
  externalOptionsMap: OptionsMap;
};

export const useSyncInitialTargeting = ({
  isLoading,
  sectionKey,
  initialState,
  externalOptionsMap,
}: UseSyncInitialTargetingProps) => {
  const set = useRequiredParamsSetter();

  useEffect(() => {
    if (isLoading) return;

    set(
      (prev: RequiredParamsType) => {
        if (!prev.sections[sectionKey]?.isLoading) return prev;

        const filteredInitialState = merge(prev.sections[sectionKey], filterOptions(initialState, externalOptionsMap));

        return {
          ...prev,
          sections: {
            ...prev.sections,
            [sectionKey]: {
              ...filteredInitialState,
              isLoading: false,
            },
          },
        };
      },
      /**
       * Hydration mirrors the rendering context the form already holds, so it must not write back to the form:
       * a rebuilt value can differ from the stored one purely by serialisation (e.g. "APP,WEB" vs "WEB,APP"),
       * which would dirty a pristine package and trigger a spurious package update on save.
       */
      { silent: true }
    );
  }, [isLoading, sectionKey, initialState, externalOptionsMap, set]);
};
